import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "@shell/apiClientContext";
import {
    createSageOffice,
    deleteSageOffice,
    restoreSageOffice,
    searchSageOffices,
    updateSageOffice,
} from "./api";
import type { SageOfficeUpsertPayload, SageOfficeView } from "./types";

export const SAGE_OFFICES_QUERY_KEY = ["admin", "sage-offices"] as const;

export interface SearchSageOfficesParams {
    readonly q: string;
    readonly includeDeleted: boolean;
}

export function useSageOffices(
    params: SearchSageOfficesParams,
): UseQueryResult<readonly SageOfficeView[], Error> {
    const client = useApiClient();
    return useQuery({
        queryKey: [...SAGE_OFFICES_QUERY_KEY, params.q, params.includeDeleted],
        queryFn: () =>
            searchSageOffices(client, { q: params.q, includeDeleted: params.includeDeleted }),
        staleTime: 30_000,
    });
}

export function useCreateSageOffice(): UseMutationResult<
    SageOfficeView,
    Error,
    SageOfficeUpsertPayload
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => createSageOffice(client, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: SAGE_OFFICES_QUERY_KEY });
        },
    });
}

export function useUpdateSageOffice(): UseMutationResult<
    SageOfficeView,
    Error,
    SageOfficeUpsertPayload
> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => updateSageOffice(client, payload),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: SAGE_OFFICES_QUERY_KEY });
        },
    });
}

export function useDeleteSageOffice(): UseMutationResult<void, Error, string> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => deleteSageOffice(client, id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: SAGE_OFFICES_QUERY_KEY });
        },
    });
}

export function useRestoreSageOffice(): UseMutationResult<SageOfficeView, Error, string> {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => restoreSageOffice(client, id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: SAGE_OFFICES_QUERY_KEY });
        },
    });
}
